import { SelectMenu } from "@gettactic/components/src/form/SelectMenu/SelectMenu";
import { TextInput } from "@gettactic/components/src/form/TextInput";
import { Drawer, DrawerProps } from "@gettactic/components/src/overlay/Drawer";
import { ModalActionButton } from "@gettactic/components/src/overlay/modals/buttons/ModalActionButton";
import { ModalCancelButton } from "@gettactic/components/src/overlay/modals/buttons/ModalCancelButton";
import { ModalConfirmButton } from "@gettactic/components/src/overlay/modals/buttons/ModalConfirmButton";
import { DownloadIcon, Upload } from "lucide-react";
import Papa from "papaparse";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { SpotShape, SpotShapeType } from "../../../../types";
import { ColorPickerInput } from "../../../ui/ColorPickerInput";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import { useMapEditorProps } from "../../contexts/MapEditorContext";
import { DEFAULT_NODE_RECT_COLOR } from "../../defines";

export interface SpotsManagerProps extends DrawerProps {}

interface SpotCSVData {
  spot_id: string;
  spot_type: SpotShapeType;
  row_index: number;
  item_index: number;
  color: string;
  text: string;
  resource_id: string;
}

interface SpotFormValues {
  spots: SpotCSVData[];
}

const classes = {
  ColorPickerPreview: "rounded w-10 h-10 border border-neutral-300",
  SpotIdText: "text-sm my-2 text-muted-foreground",
  UploadButton:
    "flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded cursor-pointer",
  ItemWrapper: "w-full mb-6 flex gap-6 items-center",
  Footer: "flex justify-between w-full"
};

export const SpotsManager: React.FC<SpotsManagerProps> = ({ ...rest }) => {
  const { setShapes, shapesRef } = useMapCanvas();
  const { resources } = useMapEditorProps();

  const form = useForm<SpotFormValues>({
    defaultValues: { spots: [] }
  });

  const typeOptions = React.useMemo(
    () =>
      Object.values(SpotShapeType).map((type) => ({
        label: type,
        value: type
      })),
    []
  );

  // Only read shapes when the drawer opens — avoids subscribing to
  // the heavy shapes context which would re-render on every shape change.
  React.useEffect(() => {
    if (!rest.isOpen) return;
    const spotShapes = shapesRef.current.filter(
      (shape) => shape.type === "spot"
    ) as SpotShape[];
    form.reset({
      spots: spotShapes.flatMap((shape) => ({
        spot_id: shape.id,
        spot_type: shape.spotType || undefined,
        row_index: 0,
        item_index: 0,
        color: shape.rectProps?.color || DEFAULT_NODE_RECT_COLOR,
        text: shape.label,
        resource_id: shape.resource_id || "-"
      }))
    });
  }, [form.reset, rest.isOpen]);

  const spots = form.getValues("spots");

  const selectedResources = form
    .watch("spots")
    .map((spot) => spot.resource_id)
    .filter(Boolean);

  /**
   * Memoize available resource options for each spot item
   */
  const filteredOptionsMap = React.useMemo(() => {
    return form.getValues().spots.reduce(
      (acc, item) => {
        acc[item.spot_id] = resources
          ? resources
              .filter(
                (resource) =>
                  resource.id === item.resource_id ||
                  !selectedResources.includes(resource.id)
              )
              .map((resource) => ({
                label: (
                  <>
                    {resource.name}{" "}
                    <span className={"text-muted-foreground/60"}>
                      {resource.id}
                    </span>
                  </>
                ),
                value: resource.id
              }))
          : [];
        return acc;
      },
      {} as Record<string, { label: React.ReactElement; value: string }[]>
    );
  }, [selectedResources, resources, spots]);

  /** Generate CSV Data */
  const handleDownloadCSV = () => {
    const csv = Papa.unparse(form.getValues().spots);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "spots_data.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /** Handle CSV Upload */
  const handleUploadCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      if (!target?.result) return;

      Papa.parse(target.result as string, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const csvData = result.data as SpotCSVData[];
          form.reset({ spots: csvData });
        }
      });
    };
    reader.readAsText(file);
  };

  const handleOnSubmit = form.handleSubmit((data) => {
    setShapes((shapes) =>
      shapes.map((shape) => {
        if (shape.type !== "spot") return shape;
        const updatedItem = data.spots.find(
          (formItem) => formItem.spot_id === shape.id
        );

        return {
          ...shape,
          ...(updatedItem
            ? {
                spotType: updatedItem.spot_type,
                rectProps: { ...shape.rectProps, color: updatedItem.color },
                textProps: { ...shape.textProps, text: updatedItem.text },
                resource_id:
                  updatedItem.resource_id !== "-"
                    ? updatedItem.resource_id
                    : undefined
              }
            : undefined)
        };
      })
    );

    rest.onOpenChange(false);
  });

  return (
    <Drawer
      title="Resources Manager"
      className="w-[70%]"
      footer={
        <div className={classes.Footer}>
          <div>
            <div className="mb-4 flex gap-4">
              <ModalCancelButton onClick={rest.onOpenChange.bind(null, false)}>
                Cancel
              </ModalCancelButton>
              <ModalActionButton
                onClick={handleDownloadCSV}
                iconLeft={<DownloadIcon />}
              >
                Download CSV
              </ModalActionButton>

              {/** biome-ignore lint/correctness/useUniqueElementIds: <explanation> */}
              <input
                type="file"
                accept=".csv"
                onChange={handleUploadCSV}
                className="hidden"
                id="upload-csv"
              />
              <label htmlFor="upload-csv" className={classes.UploadButton}>
                <Upload />
                Upload CSV
              </label>
            </div>
          </div>
          <div>
            <ModalConfirmButton
              onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                event.preventDefault();
                event.stopPropagation();
                handleOnSubmit().then();
              }}
            >
              Apply
            </ModalConfirmButton>
          </div>
        </div>
      }
      {...rest}
    >
      <div>
        {form.getValues().spots.map((item, index) => (
          <div key={index}>
            <div className={classes.SpotIdText}>{item.spot_id}</div>
            <div className={classes.ItemWrapper}>
              <Controller
                control={form.control}
                name={`spots.${index}.color`}
                render={({ field }) => (
                  <ColorPickerInput
                    value={field.value}
                    onChange={field.onChange}
                    showInput={false}
                  />
                )}
              />
              <Controller
                control={form.control}
                name={`spots.${index}.spot_type`}
                render={({ field }) => (
                  <SelectMenu
                    className={"w-full"}
                    options={typeOptions}
                    values={field.value ? [field.value] : []}
                    onChange={(values) => field.onChange(values[0])}
                    maxAllowed={1}
                    triggerMode={"click"}
                  />
                )}
              />
              <TextInput {...form.register(`spots.${index}.text`)} />
              <div className={"w-full"}>
                <Controller
                  control={form.control}
                  name={`spots.${index}.resource_id`}
                  render={({ field }) => (
                    <SelectMenu
                      className={"w-full"}
                      options={filteredOptionsMap[item.spot_id] || []}
                      values={field.value ? [field.value] : []}
                      onChange={(values) => field.onChange(values[0])}
                      maxAllowed={1}
                      triggerMode={"click"}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  );
};
